import React, { useState, Component } from 'react';
import { X, Building2, Mail, Upload } from 'lucide-react';
import { allCategories } from '../utils/partnersData';
import { useAdminData } from '../contexts/AdminDataContext';
interface PartnerCreateDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}
const PartnerCreateDrawer: React.FC<PartnerCreateDrawerProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const {
    createPartner
  } = useAdminData();
  const [formData, setFormData] = useState({
    name: '',
    website: '',
    email: '',
    description: '',
    categories: [] as string[],
    locations: ''
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createPartner({
      name: formData.name,
      logo: `https://via.placeholder.com/200x80/10b981/ffffff?text=${encodeURIComponent(formData.name)}`,
      website: formData.website,
      email: formData.email,
      description: formData.description,
      categories: formData.categories,
      locations: parseInt(formData.locations) || 1,
      activeRewards: 0,
      activeUsers: 0,
      totalRedemptions: 0,
      status: 'active'
    });
    setFormData({
      name: '',
      website: '',
      email: '',
      description: '',
      categories: [],
      locations: ''
    });
    onSuccess?.();
    onClose();
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  const handleCategoryToggle = (category: string) => {
    setFormData({
      ...formData,
      categories: formData.categories.includes(category) ? formData.categories.filter(c => c !== category) : [...formData.categories, category]
    });
  };
  if (!isOpen) return null;
  return <>
      {/* Backdrop with blur */}
      <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40" onClick={onClose} data-id="element-990" />

      {/* Drawer */}
      <div className={`fixed top-0 right-0 h-full w-full sm:w-[600px] bg-white shadow-2xl z-50 overflow-y-auto transition-transform duration-300 ease-in-out ${isOpen ? 'transform-none' : 'translate-x-full'}`} data-id="element-991">
        <div className="p-6" data-id="element-992">
          {/* Header */}
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200" data-id="element-993">
            <div data-id="element-994">
              <h2 className="text-xl font-semibold text-gray-900" data-id="element-995">
                Aggiungi Nuovo Partner
              </h2>
              <p className="text-sm font-normal text-gray-600 mt-1" data-id="element-996">
                Invita un nuovo partner sulla piattaforma
              </p>
            </div>
            <button onClick={onClose} className="rounded-full p-2 hover:bg-gray-100 transition-colors" data-id="element-997">
              <X className="h-5 w-5 text-gray-500" data-id="element-998" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5" data-id="element-999">
            {/* Nome Partner */}
            <div data-id="element-1000">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1001">
                Nome Partner <span className="text-red-500" data-id="element-1002">*</span>
              </label>
              <input type="text" name="name" value={formData.name} onChange={handleChange} required placeholder="Es. La Saponaria" className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm font-normal focus:ring-2 focus:ring-green-600 focus:border-transparent" data-id="element-1003" />
            </div>

            {/* Website e Email */}
            <div className="grid grid-cols-2 gap-4" data-id="element-1004">
              <div data-id="element-1005">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1006">
                  Website <span className="text-red-500" data-id="element-1007">*</span>
                </label>
                <input type="url" name="website" value={formData.website} onChange={handleChange} required placeholder="https://www.example.it" className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm font-normal focus:ring-2 focus:ring-green-600 focus:border-transparent" data-id="element-1008" />
              </div>

              <div data-id="element-1009">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1010">
                  Email <span className="text-red-500" data-id="element-1011">*</span>
                </label>
                <input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="contatti@partner.it" className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm font-normal focus:ring-2 focus:ring-green-600 focus:border-transparent" data-id="element-1012" />
              </div>
            </div>

            {/* Descrizione */}
            <div data-id="element-1013">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1014">
                Descrizione <span className="text-red-500" data-id="element-1015">*</span>
              </label>
              <textarea name="description" value={formData.description} onChange={handleChange} required rows={3} placeholder="Descrivi brevemente il partner e i suoi prodotti/servizi..." className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm font-normal focus:ring-2 focus:ring-green-600 focus:border-transparent resize-none" data-id="element-1016" />
            </div>

            {/* Numero Sedi */}
            <div data-id="element-1017">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1018">
                Numero di Sedi
              </label>
              <input type="number" name="locations" value={formData.locations} onChange={handleChange} min="1" placeholder="Es. 5" className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm font-normal focus:ring-2 focus:ring-green-600 focus:border-transparent" data-id="element-1019" />
            </div>

            {/* Categorie */}
            <div data-id="element-1020">
              <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-1021">
                Categorie <span className="text-red-500" data-id="element-1022">*</span>
              </label>
              <div className="grid grid-cols-2 gap-3 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3" data-id="element-1023">
                {allCategories.map(category => <label key={category} className="flex items-center gap-2 cursor-pointer" data-id="element-1024">
                    <input type="checkbox" checked={formData.categories.includes(category)} onChange={() => handleCategoryToggle(category)} className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-600" data-id="element-1025" />
                    <span className="text-sm font-normal text-gray-700" data-id="element-1026">
                      {category}
                    </span>
                  </label>)}
              </div>
              {formData.categories.length === 0 && <p className="text-sm font-normal text-red-500 mt-2" data-id="element-1027">
                  Seleziona almeno una categoria
                </p>}
            </div>

            {/* Logo Upload */}
            <div data-id="element-1028">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1029">
                Logo Partner
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-green-600 transition-colors cursor-pointer" data-id="element-1030">
                <Upload className="h-6 w-6 text-gray-400 mx-auto mb-2" data-id="element-1031" />
                <p className="text-sm font-normal text-gray-600 mb-1" data-id="element-1032">
                  Clicca per caricare il logo
                </p>
                <p className="text-xs font-normal text-gray-500" data-id="element-1033">
                  PNG, JPG o SVG (max 2MB)
                </p>
                <input type="file" accept="image/*" className="hidden" id="logo-upload" data-id="element-1034" />
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-1035">
              <div className="flex items-start gap-3" data-id="element-1036">
                <Mail className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-1037" />
                <div data-id="element-1038">
                  <h4 className="font-semibold text-blue-900 mb-1 text-sm" data-id="element-1039">
                    Email di Invito
                  </h4>
                  <p className="text-sm font-normal text-blue-700" data-id="element-1040">
                    Dopo aver creato il partner, verrà inviata automaticamente
                    un'email di invito con le istruzioni per accedere al
                    pannello partner di Greenback.
                  </p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t border-gray-200" data-id="element-1041">
              <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors" data-id="element-1042">
                Annulla
              </button>
              <button type="submit" disabled={formData.categories.length === 0} className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1043">
                <Building2 className="h-4 w-4" data-id="element-1044" />
                Crea e Invia Invito
              </button>
            </div>
          </form>
        </div>
      </div>
    </>;
};
export default PartnerCreateDrawer;